import { extendApi, extendZodWithOpenApi } from '@anatine/zod-openapi'
import { z } from 'zod'
import { Brand } from '../../shared'
import { BaseBecoolWebhookEvent, BecoolDeliveryCompletedEvent, BecoolDeliveryExceptionEvent, BecoolDeliveryScheduledEvent, BecoolOutForDeliveryEvent, BecoolReceivedAtOriginFacilityEvent, BecoolWebhookEventType } from './becool-webhook-events'

extendZodWithOpenApi(z)

/**
 * Supported courier providers.
 */
export const Courier = z.enum(['becool']).describe('Courier provider to fulfil this shipment')
export type Courier = z.infer<typeof Courier>

/**
 * Delivery slot window - morning or afternoon.
 * Zod schema version for courier service usage (provides .options for TypeORM enum columns).
 */
export const DeliverySlotWindowSchema = z.enum(['AM', 'PM']).describe('Delivery slot window, morning or afternoon')
export type DeliverySlotWindowValue = z.infer<typeof DeliverySlotWindowSchema>

/**
 * Input for fetching delivery details for multiple shipments.
 */
export const ShipmentDeliveryDetailsInput = z.array(z.string().cuid2()).describe('Array of Shipment IDs')
export type ShipmentDeliveryDetailsInput = z.infer<typeof ShipmentDeliveryDetailsInput>

export const Address = z
  .object({
    address1: z.string(),
    address2: z.string().nullable().optional(),
    city: z.string(),
    state: z.string().toUpperCase(),
    postcode: z.string(),
    companyName: z.string().nullable().optional(),
  })
  .describe('Delivery Address')
export type Address = z.infer<typeof Address>

export const Contact = z
  .object({
    phone1: z.string().min(1).nullish(),
    phone2: z.string().min(1).nullish(),
    email: z.string().email(),
  })
  .describe('Customer Contact information')
export type Contact = z.infer<typeof Contact>

export const CustomerDetails = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1),
    contact: Contact,
    address: Address,
  })
  .describe('Customer Details')
export type CustomerDetails = z.infer<typeof CustomerDetails>

export const DeliveryPreference = z
  .object({
    instructions: z.string().describe('Delivery instructions'),
    date: z.string().date().describe('Delivery date in YYYY-MM-DD format'),
    timePreference: z.enum(['AM', 'PM']),
  })
  .describe('Delivery Preference')
export type DeliveryPreference = z.infer<typeof DeliveryPreference>

export const ShipmentStatus = z.enum(['pending', 'pending_manifest', 'manifest_cancelled', 'cancelled', 'delivered']).describe('Shipment Status')
export type ShipmentStatus = z.infer<typeof ShipmentStatus>

/** Lightweight shipment lookup for cross-service gating — status only, no order-service enrichment. */
export const ShipmentStatusResponseSchema = z.object({
  id: z.string().cuid2().describe('Shipment ID'),
  status: ShipmentStatus,
})
export type ShipmentStatusResponse = z.infer<typeof ShipmentStatusResponseSchema>

export const CheckServiceableInputSchema = z.object({
  postcode: z.string().min(4).max(4).describe('Australian postcode (4 digits)'),
  suburb: z.string().trim().min(1).describe('Suburb name'),
  brand: z.nativeEnum(Brand).default(Brand.LYKA).describe('Brand to check serviceability for'),
})
export type CheckServiceableInput = z.infer<typeof CheckServiceableInputSchema>

export const CheckServiceableResponseSchema = z.object({
  serviceable: z.boolean().describe('Whether the location is serviceable for the specified brand'),
})
export type CheckServiceableResponse = z.infer<typeof CheckServiceableResponseSchema>

export const Shipment = z
  .object({
    status: ShipmentStatus,
    id: z.string().cuid2().describe('Shipment ID'),
    orderId: z.string().nullable().optional(),
    invoiceNumber: z.string().describe('Courier Invoice ID'),
    trackingNumber: z.string().describe('Tracking ID, generated by the courier'),
    createdAt: z.string().datetime().describe('Date Shipment was created in UTC'),
    deliveryAddress: Address,
    deliveryContactName: z.string().describe('Customer name for delivery'),
    deliveryContactNumber: z.string().describe('Customer number for delivery'),
    deliveryContactEmail: z.string().describe('Customer email for delivery'),
    deliveryDate: z.string().datetime().describe('Scheduled delivery date in UTC'),
    deliveryInstructions: z.string().describe('Delivery instructions'),
    deliveryTimeWindowStart: z.string().describe('Scheduled start of delivery time window in local time'),
    deliveryTimeWindowEnd: z.string().describe('Scheduled start of delivery time window in local time'),
    makeDate: z.string().datetime().describe('Scheduled make date at Lyka facility in UTC'),
    dispatchDate: z
      .string()
      .datetime()
      .nullable()
      .optional()
      .describe('Scheduled dispatch date time at Lyka facility in UTC'),
    boxes: z.array(
      z.object({
        boxId: z.string(),
        serialNumber: z.string(),
      }),
    ),
  })
  .describe('Shipment')
export type Shipment = z.infer<typeof Shipment>

export const CreateShipmentResponse = z
  .object({
    delivery: Shipment,
  })
  .describe('Create Shipment Response')
export type CreateShipmentResponse = z.infer<typeof CreateShipmentResponse>

export const ShipmentDeliveryDetailResponse = z
  .object({
    id: z.string(),
    deliveryContactName: z.string().describe('Customer name for delivery'),
    deliveryAddress: Address,
    deliveryDate: z.string().datetime().describe('Scheduled delivery date in UTC'),
    deliverySlot: z.enum(['AM', 'PM']).describe('Delivery time slot'),
  })
  .describe('Shipment Delivery Detail')
export type ShipmentDeliveryDetailResponse = z.infer<typeof ShipmentDeliveryDetailResponse>

export const ShipmentDeliveryDetailsResponse = z
  .array(ShipmentDeliveryDetailResponse)
  .describe('Array of Shipment Delivery Details')
export type ShipmentDeliveryDetailsResponse = z.infer<typeof ShipmentDeliveryDetailsResponse>

export const BeCoolDepotCode = z.enum([
  'ADL',
  'ALB',
  'CFS',
  'HBA',
  'MEL',
  'MIL',
  'BDB',
  'BNE',
  'CBR',
  'CNS',
  'MKY',
  'NTL',
  'PER',
  'OAG',
  'ROK',
  'SYD',
  'TSV',
  'MGB'
])

export type BeCoolDepotCode = z.infer<typeof BeCoolDepotCode>

export const PostcodeFacilityResponse = z.object({
  facilityId: z.number().describe('Facility ID to send the shipment to'),
  depotCode: BeCoolDepotCode.describe('Depot code for shipping purposes'),
})

export type PostcodeFacilityResponse = z.infer<typeof PostcodeFacilityResponse>

export const PostcodeSuburbsResponse = z.object({
  shippingZoneId: z.number().describe('Shipping zone ID'),
  name: z.string().describe('Shipping zone name'),
  postcode: z.string().describe('Postcode'),
  suburb: z.string().describe('Suburb'),
  isServiceable: z.boolean().describe('Is the suburb serviceable'),
  cost: z.number().describe('Shipping zone cost'),
  customCost: z.number().nullable().describe('Shipping zone custom cost'),
})
export type PostcodeSuburbsResponse = z.infer<typeof PostcodeSuburbsResponse>

export const PostcodeShippingZonesResponse = z.object({
  id: z.number(),
  depotCode: z.string(),
  depotName: z.string(),
  name: z.string(),
  state: z.string(),
  createdTimeUTC: z.string(),
  cost: z.number().describe('Shipping zone cost'),
  customCost: z.number().nullable().describe('Shipping zone custom cost'),
})
export type PostcodeShippingZonesResponse = z.infer<typeof PostcodeShippingZonesResponse>

export const CreatePostcodeInputSchema = extendApi(
  z.object({
    postcode: z.string(),
    suburb: z.string(),
  }),
  {
    title: 'Create Postcode & Suburb input schema',
    example: {
      postcode: '3000',
      suburb: 'Melbourne',
    },
  },
)

export type CreatePostcodeInputDTO = z.infer<typeof CreatePostcodeInputSchema>

export const CreatePostcodeResponseSchema = z.object({
  id: z.string(),
  postcode: z.string(),
  suburb: z.string(),
  active: z.boolean(),
  shippingZone: z.object({
    id: z.string(),
    name: z.string(),
    state: z.string(),
    depotCode: z.string(),
    depotName: z.string(),
  }),
})
export type CreatePostcodeResponseDTO = z.infer<typeof CreatePostcodeResponseSchema>

export const CreateBecoolDeliveryCompletedInput = z.object({
  trackingId: z.string().describe('Courier Tracking ID'),
  invoiceNumber: z.string().regex(/^(LYKA|TYGA)/, 'Invoice number must start with LYKA or TYGA').describe('Invoice Number'),
  eventType: z.literal(BecoolWebhookEventType.enum.delivery_completed),
  payload: BecoolDeliveryCompletedEvent,
})
export type CreateBecoolDeliveryCompletedInput = z.infer<typeof CreateBecoolDeliveryCompletedInput>

export const CreateBecoolDeliveryScheduledInput = z.object({
  trackingId: z.string().describe('Courier Tracking ID'),
  invoiceNumber: z.string().regex(/^(LYKA|TYGA)/, 'Invoice number must start with LYKA or TYGA').describe('Invoice Number'),
  eventType: z.literal(BecoolWebhookEventType.enum.delivery_scheduled),
  payload: BecoolDeliveryScheduledEvent,
})
export type CreateBecoolDeliveryScheduledInput = z.infer<typeof CreateBecoolDeliveryScheduledInput>

export const CreateBecoolDeliveryExceptionInput = z.object({
  trackingId: z.string().describe('Courier Tracking ID'),
  invoiceNumber: z.string().regex(/^(LYKA|TYGA)/, 'Invoice number must start with LYKA or TYGA').describe('Invoice Number'),
  eventType: z.literal(BecoolWebhookEventType.enum.delivery_exception),
  payload: BecoolDeliveryExceptionEvent,
})
export type CreateBecoolDeliveryExceptionInput = z.infer<typeof CreateBecoolDeliveryExceptionInput>

export const CreateBecoolUnknownEventReceivedInput = z.object({
  trackingId: z.string().describe('Courier Tracking ID'),
  invoiceNumber: z.string().regex(/^(LYKA|TYGA)/, 'Invoice number must start with LYKA or TYGA').describe('Invoice Number'),
  eventType: z.literal(BecoolWebhookEventType.enum.unknown_event_received),
  payload: BaseBecoolWebhookEvent,
})
export type CreateBecoolUnknownEventReceivedInput = z.infer<typeof CreateBecoolUnknownEventReceivedInput>

export const CreateBecoolOutForDeliveryInput = z.object({
  trackingId: z.string().describe('Courier Tracking ID'),
  invoiceNumber: z.string().regex(/^(LYKA|TYGA)/, 'Invoice number must start with LYKA or TYGA').describe('Invoice Number'),
  eventType: z.literal(BecoolWebhookEventType.enum.out_for_delivery),
  payload: BecoolOutForDeliveryEvent,
})
export type CreateBecoolOutForDeliveryInput = z.infer<typeof CreateBecoolOutForDeliveryInput>

export const CreateBecoolReceivedAtOriginFacilityInput = z.object({
  trackingId: z.string().describe('Courier Tracking ID'),
  invoiceNumber: z.string().regex(/^(LYKA|TYGA)/, 'Invoice number must start with LYKA or TYGA').describe('Invoice Number'),
  eventType: z.literal(BecoolWebhookEventType.enum.received_at_origin_facility),
  payload: BecoolReceivedAtOriginFacilityEvent,
})
export type CreateBecoolReceivedAtOriginFacilityInput = z.infer<typeof CreateBecoolReceivedAtOriginFacilityInput>

export const CreateCourierEventInput = z.discriminatedUnion(
  'eventType',
  [
    CreateBecoolDeliveryCompletedInput,
    CreateBecoolDeliveryScheduledInput,
    CreateBecoolDeliveryExceptionInput,
    CreateBecoolUnknownEventReceivedInput,
    CreateBecoolOutForDeliveryInput,
    CreateBecoolReceivedAtOriginFacilityInput,
  ],
  {
    message: 'Event Type and Payload must match.',
  },
)
export type CreateCourierEventInput = z.infer<typeof CreateCourierEventInput>

export const CreateCourierEventOutput = z.object({
  id: z.string().describe('Event ID'),
})
export type CreateCourierEventOutput = z.infer<typeof CreateCourierEventOutput>

export const SearchShipmentInput = z.object({
  trackingNumber: Shipment.shape.trackingNumber.optional(),
  invoiceNumber: Shipment.shape.invoiceNumber.optional(),
})
export type SearchShipmentInput = z.infer<typeof SearchShipmentInput>

export const PaginatedShipment = z.object({
  total: z.number().int().nonnegative(),
  items: z.array(Shipment),
})
export type PaginatedShipment = z.infer<typeof PaginatedShipment>

/**
 * Input for creating a shipment in the courier service.
 * Replaces the old CreateShipmentInput from lyka-shared-types.
 */
export const CreateShipmentInputSchema = z
  .object({
    customer: CustomerDetails,
    orderId: z.string().optional(),
    makeDate: z.string().optional(),
    delivery: DeliveryPreference.extend({
      boxIds: z.array(z.string()),
    }),
    skipManifestUpload: z
      .boolean()
      .optional()
      .describe('When true, creates a pending shipment without uploading manifest to BeCool'),
    brand: z.nativeEnum(Brand).describe('Brand is required for correct cutoff schedule filtering'),
  })
  .describe('Create Shipment Input')
export type CreateShipmentInputSchema = z.infer<typeof CreateShipmentInputSchema>

export const DeliveryScheduleQueryInput = z.object({
  postcode: z.string(),
  suburb: z.string(),
  fromDate: z.string().date().optional(),
  toDate: z.string().date().optional(),
  skipBillingDateCheck: z.enum(['0', '1']).optional(),
  /** @deprecated Use skipBillingDateCheck instead */
  skipCutoffChecks: z.enum(['0', '1']).optional(),
  brand: z.nativeEnum(Brand).optional().default(Brand.LYKA),
})

export type DeliveryScheduleQueryInput = z.infer<typeof DeliveryScheduleQueryInput>

export const CutoffsSchema = z.object({
  manifestDate: z.string().datetime({ offset: true }),
  dispatchDate: z.string().datetime({ offset: true }),
  lykaDispatchDate: z.string().datetime({ offset: true }),
  factoryCutOffDate: z.string().datetime({ offset: true }).nullable().optional(),
})

export const DeliverySlotSchema = z.object({
  id: z.enum(['AM', 'PM']),
  name: z.enum(['AM', 'PM']),
  start: z.enum(['00:00', '08:00']),
  end: z.enum(['07:00', '18:00']),
  label: z.string(),
  note: z.string().nullable(),
  makeDate: z.string().datetime({ offset: true }),
  billingDate: z.string().datetime({ offset: true }),
  cutoffs: CutoffsSchema,
})

export const DeliveryScheduleDateSchema = z.object({
  date: z.string().date(),
  slots: z.array(DeliverySlotSchema),
})

export const DeliveryScheduleResult = z.object({
  data: z.array(DeliveryScheduleDateSchema),
  timezone: z.string(),
})

export type DeliveryScheduleResult = z.infer<typeof DeliveryScheduleResult>

export const CutoffDatesQueryInput = z.object({
  deliveryDate: z.string().date(),
  deliverySlotWindow: z.enum(['AM', 'PM']).default('AM'),
  skipBillingDateCheck: z.enum(['0', '1']).optional(),
  brand: z.nativeEnum(Brand).optional().default(Brand.LYKA),
})

export type CutoffDatesQueryInput = z.infer<typeof CutoffDatesQueryInput>

export const CutoffDatesPathParams = z.object({
  postcode: z.string(),
  suburb: z.string(),
})

export type CutoffDatesPathParams = z.infer<typeof CutoffDatesPathParams>

export const CutoffDatesResult = z.object({
  billingDate: z.string().datetime({ offset: true }),
  makeDate: z.string().datetime({ offset: true }),
  dispatchCutoffDate: z.string().datetime({ offset: true }),
  manifestCutoffDate: z.string().datetime({ offset: true }),
  lykaDispatchCutoffDate: z.string().datetime({ offset: true }),
  factoryCutOffDate: z.string().datetime({ offset: true }).nullable().optional(),
})

export type CutoffDatesResult = z.infer<typeof CutoffDatesResult>

export const ShipmentsUnmanifestedQueryInput = z.object({
  makeDateFrom: z.string().date(),
  makeDateTo: z.string().date(),
  staleAfterMinutes: z.number().optional().default(15),
})

export type ShipmentsUnmanifestedQueryInput = z.infer<typeof ShipmentsUnmanifestedQueryInput>

export const UnmanifestedShipmentSchema = z.object({
  shipmentId: z.string().cuid2().describe('Shipment ID'),
  orderId: z.string().nullable().describe('Phoenix order ID'),
  invoiceNumber: z.string().describe('Courier invoice number'),
  makeDate: z.string().datetime().describe('Scheduled make date in UTC'),
  deliveryDate: z.string().datetime().describe('Scheduled delivery date in UTC'),
  createdAt: z.string().datetime().describe('Date shipment was created in UTC'),
})

/**
 * Response for unmanifested shipments endpoint.
 */
export const UnmanifestedShipmentsResponse = extendApi(
  z.object({
    total: z.number().int().nonnegative().describe('Total number of unmanifested shipments'),
    items: z.array(UnmanifestedShipmentSchema),
  }),
)

export type UnmanifestedShipmentsResponse = z.infer<typeof UnmanifestedShipmentsResponse>

/**
 * Patch shipment tracking and/or status (courier DB only; no BeCool manifest calls).
 */
export const UpdateShipmentTrackingStatusInputSchema = z
  .object({
    trackingNumber: z.string().min(1).optional().describe('Tracking number to store on the shipment'),
    status: ShipmentStatus.optional().describe('Shipment status to set'),
  })
  .refine(
    data => data.trackingNumber !== undefined || data.status !== undefined,
    { message: 'At least one of trackingNumber or status is required' },
  )
  .describe('Update shipment tracking/status input')

export type UpdateShipmentTrackingStatusInput = z.infer<typeof UpdateShipmentTrackingStatusInputSchema>

export const UpdateShipmentTrackingStatusResponseSchema = z
  .object({
    shipmentId: z.string().cuid2(),
    oldTrackingNumber: z.string(),
    newTrackingNumber: z.string(),
    oldStatus: ShipmentStatus,
    newStatus: ShipmentStatus,
    updatedAt: z.string().datetime(),
  })
  .describe('Update shipment tracking/status response')

export type UpdateShipmentTrackingStatusResponse = z.infer<typeof UpdateShipmentTrackingStatusResponseSchema>

/**
 * Input for checking delivery schedule by postcode and suburb.
 */
/**
 * Response for cancelling a shipment (with or without a BeCool manifest).
 */
export const CancelShipmentResponseSchema = z
  .object({
    shipmentId: z.string().cuid2(),
    previousStatus: ShipmentStatus,
    newStatus: ShipmentStatus,
    manifestCancelled: z.boolean().describe('Whether a BeCool manifest was cancelled'),
    cancelledAt: z.string().datetime(),
  })
  .describe('Cancel Shipment Response')

export type CancelShipmentResponse = z.infer<typeof CancelShipmentResponseSchema>

export const DeliveryScheduleInputSchema = z
  .object({
    postcode: z.string().min(1).describe('Australian postcode'),
    suburb: z.string().min(1).describe('Suburb name'),
  })
  .describe('Delivery schedule input')

export type DeliveryScheduleInput = z.infer<typeof DeliveryScheduleInputSchema>

/**
 * Cutoff day reference with day of week and week offset.
 */
export const CutoffDaySchema = z.object({
  dayOfWeek: z.number().int().min(0).max(7).describe('Day of week (0 = Sunday, 7 = Sunday wrap)'),
  weekOffset: z.number().int().describe('Week offset from delivery week'),
})

export type CutoffDay = z.infer<typeof CutoffDaySchema>

/**
 * Delivery slot configuration for a specific day and time window.
 */
export const DeliverySlotConfigSchema = z.object({
  dayOfWeek: z.number().int().min(0).max(7).describe('Delivery day of week'),
  deliverySlotWindow: DeliverySlotWindowSchema,
  billingCutoffDay: CutoffDaySchema,
  dispatchCutoffDay: CutoffDaySchema,
  manifestCutoffDay: CutoffDaySchema,
  makeCutoffDay: CutoffDaySchema,
  packingCutoffDay: CutoffDaySchema,
  dispatchDayDeltaInSeconds: z.number().int().describe('Dispatch delta in seconds'),
  manifestDayDeltaInSeconds: z.number().int().describe('Manifest delta in seconds'),
  makeDayDeltaInSeconds: z.number().int().describe('Make delta in seconds'),
  packingDayDeltaInSeconds: z.number().int().describe('Packing delta in seconds'),
  billingDayDeltaInSeconds: z.number().int().describe('Billing delta in seconds'),
})

export type DeliverySlotConfig = z.infer<typeof DeliverySlotConfigSchema>

/**
 * Response for delivery schedule check.
 */
export const DeliveryScheduleResponseSchema = z
  .object({
    zoneId: z.number().int().describe('Shipping zone ID'),
    slots: z.array(DeliverySlotConfigSchema).describe('Available delivery slot configurations'),
  })
  .describe('Delivery schedule response')

export type DeliveryScheduleResponse = z.infer<typeof DeliveryScheduleResponseSchema>

export const ActivePhoenixPostcodeSchema = z.object({
  postcode: z.string(),
  suburb: z.string(),
  shippingZoneId: z.string(),
  shippingZoneName: z.string(),
  shippingZoneState: z.string(),
  depotCode: z.string(),
})
export type ActivePhoenixPostcode = z.infer<typeof ActivePhoenixPostcodeSchema>

export const ActivePhoenixPostcodesResponseSchema = z.object({
  items: z.array(ActivePhoenixPostcodeSchema),
  total: z.number().int().nonnegative(),
})
export type ActivePhoenixPostcodesResponse = z.infer<typeof ActivePhoenixPostcodesResponseSchema>

/**
 * How a free-text suburb matches our shipping_postcodes rows, in the order the
 * resolver tries them. Read-only: nothing is corrected on the back of it.
 */
export const SuburbMatchMethodSchema = z.enum([
  'exact', // trim + uppercase equals an active row
  'exact_inactive', // equals a row that is switched off
  'normalised', // equal once case, spaces, punctuation and MT/ST/CRK-style abbreviations are ignored
  'levenshtein', // one edit away from exactly one candidate on the postcode
  'ambiguous', // several candidates within one edit, or twin spellings in different depots
  'other_postcode', // the exact suburb exists, but on another active postcode in the same state
  'absent', // the postcode has rows, none close
  'postcode_unknown', // no rows at all for the postcode
])
export type SuburbMatchMethod = z.infer<typeof SuburbMatchMethodSchema>

export const ResolveSuburbQuerySchema = z.object({
  postcode: z.string().trim().min(1),
  suburb: z.string().trim().min(1),
})
export type ResolveSuburbQuery = z.infer<typeof ResolveSuburbQuerySchema>

export const SuburbResolutionSchema = z.object({
  input: z.object({ postcode: z.string(), suburb: z.string() }),
  method: SuburbMatchMethodSchema,
  /** Our row's spelling, uppercase as stored. Null unless method is exact, exact_inactive, normalised or levenshtein. */
  matched: z.string().nullable(),
  /** Every row in play when method is ambiguous. Empty otherwise. */
  candidates: z.array(z.string()),
  /** Active postcodes in the same state (same first digit) holding the exact suburb, when method is other_postcode. Empty otherwise. */
  otherPostcodes: z.array(z.string()),
  /** Active suburbs on the postcode, so a human can eyeball the alternatives. */
  activeSuburbsOnPostcode: z.array(z.string()),
})
export type SuburbResolution = z.infer<typeof SuburbResolutionSchema>

export const BecoolZoneLocationSchema = z.object({
  suburb: z.string(),
  postcode: z.string(),
})
export type BecoolZoneLocation = z.infer<typeof BecoolZoneLocationSchema>

export const BecoolZoneWithLocationsSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  regionId: z.number().int(),
  regionName: z.string(),
  depotId: z.number().int(),
  depotName: z.string(),
  depotCode: z.string(),
  locations: z.array(BecoolZoneLocationSchema),
})
export type BecoolZoneWithLocations = z.infer<typeof BecoolZoneWithLocationsSchema>
